import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { UploadCloud, X } from "lucide-react";
import { apiGet, apiPatch, apiUpload, ApiError } from "../lib/api.js";

/**
 * Brand profile — logos, colours, fonts shared by every form (docs/03-API-SPEC.md §4,
 * superadmin-only). One profile, `isDefault: true`, seeded as "Seplat / NNPC JV"
 * (packages/db/prisma/seed.ts) — its logo fields start empty, so every form's header falls back
 * to text wordmarks (apps/public/src/FormChrome.tsx) until real files are uploaded here.
 */

interface BrandProfile {
  id: string;
  name: string;
  primaryLogoId: string | null;
  secondaryLogoId: string | null;
  faviconId: string | null;
  logoLayout: string;
  colors: Record<string, string>;
  isDefault: boolean;
}

const labelClass = "mb-1 block text-xs font-medium uppercase tracking-wide text-muted-foreground";

const LOGO_SLOTS = [
  { key: "primaryLogoId" as const, label: "Primary mark", helper: "Seplat Energy" },
  { key: "secondaryLogoId" as const, label: "Secondary mark", helper: "NNPC" },
  { key: "faviconId" as const, label: "Favicon", helper: "Browser tab icon" },
];

const LogoSlot = ({
  slotLabel,
  helper,
  assetId,
  onUpload,
  onClear,
  uploading,
}: {
  slotLabel: string;
  helper: string;
  assetId: string | null;
  onUpload: (file: File) => void;
  onClear: () => void;
  uploading: boolean;
}): JSX.Element => {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="rounded-lg border bg-card p-4">
      <p className="text-sm font-medium">{slotLabel}</p>
      <p className="mb-3 text-xs text-muted-foreground">{helper}</p>

      <div className="flex h-24 items-center justify-center rounded-md border border-dashed bg-muted/30">
        {assetId ? (
          <img
            src={`/api/v1/assets/${assetId}`}
            alt={`${slotLabel} preview`}
            className="max-h-20 max-w-full object-contain"
          />
        ) : (
          <UploadCloud aria-hidden className="h-6 w-6 text-muted-foreground" />
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="sr-only"
        aria-label={`Upload ${slotLabel}`}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) onUpload(file);
          event.target.value = "";
        }}
      />

      <div className="mt-3 flex gap-2">
        <button
          type="button"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          className="flex-1 rounded-md border px-2 py-1.5 text-xs font-medium hover:bg-muted disabled:opacity-50"
        >
          {uploading ? "Uploading…" : assetId ? "Replace" : "Upload"}
        </button>
        {assetId && (
          <button
            type="button"
            onClick={onClear}
            aria-label={`Remove ${slotLabel}`}
            className="rounded-md border px-2 py-1.5 text-xs hover:bg-muted"
          >
            <X aria-hidden className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <p className="mt-2 text-[11px] text-muted-foreground">PNG, JPEG or WebP, up to 5 MB.</p>
    </div>
  );
};

export const BrandProfilePage = (): JSX.Element => {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [uploadingSlot, setUploadingSlot] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ["brand-profiles"],
    queryFn: () => apiGet<{ data: BrandProfile[] }>("/brand-profiles"),
  });

  const profile = (query.data?.data ?? []).find((p) => p.isDefault) ?? query.data?.data[0];

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["brand-profiles"] });

  const patch = useMutation({
    mutationFn: (data: Partial<BrandProfile>) => apiPatch(`/brand-profiles/${profile!.id}`, data),
    onSuccess: invalidate,
  });

  const uploadTo = async (slot: (typeof LOGO_SLOTS)[number]["key"], file: File): Promise<void> => {
    if (!profile) return;
    setError(null);
    setUploadingSlot(slot);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("kind", "logo");
      const uploaded = await apiUpload<{ id: string }>("/assets", formData);
      await patch.mutateAsync({ [slot]: uploaded.id });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Upload failed. Try again.");
    } finally {
      setUploadingSlot(null);
    }
  };

  if (query.isPending) {
    return <p className="text-sm text-muted-foreground">Loading brand profile…</p>;
  }

  if (!profile) {
    return (
      <p className="rounded-lg border bg-card p-4 text-sm text-muted-foreground">
        No brand profile exists yet.
      </p>
    );
  }

  return (
    <div className="max-w-3xl space-y-6">
      <header>
        <h2 className="font-heading text-xl font-semibold">Brand profile</h2>
        <p className="text-sm text-muted-foreground">
          {profile.name} — shared by every form unless a form sets its own logo layout in Design.
        </p>
      </header>

      {error && (
        <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </p>
      )}

      <section className="grid gap-4 sm:grid-cols-3">
        {LOGO_SLOTS.map((slot) => (
          <LogoSlot
            key={slot.key}
            slotLabel={slot.label}
            helper={slot.helper}
            assetId={profile[slot.key]}
            uploading={uploadingSlot === slot.key}
            onUpload={(file) => void uploadTo(slot.key, file)}
            onClear={() => patch.mutate({ [slot.key]: null })}
          />
        ))}
      </section>

      <section className="rounded-lg border bg-card p-4">
        <label htmlFor="default-logo-layout" className={labelClass}>
          Default logo layout
        </label>
        <select
          id="default-logo-layout"
          className="w-full max-w-xs rounded-md border px-2 py-1.5 text-sm"
          value={profile.logoLayout}
          onChange={(event) => patch.mutate({ logoLayout: event.target.value })}
        >
          <option value="side_by_side">Side by side</option>
          <option value="stacked">Stacked</option>
          <option value="primary_only">Primary mark only</option>
          <option value="secondary_only">Secondary mark only</option>
        </select>
        <p className="mt-2 text-xs text-muted-foreground">
          A form can override this in its own Design tab; this is what a new form inherits.
        </p>
      </section>
    </div>
  );
};
